import {
    normalizeBookingPortalVariant,
    type BookingPortalVariant,
} from "./bookingPortal";

/**
 * Business-facing booking profiles supported by the shared booking portal.
 *
 * A profile describes what is being booked and which information the flow
 * needs. It deliberately does not describe colors, layout, or component
 * presentation; those remain owned by BookingPortalVariant.
 */
export const BOOKING_PORTAL_PROFILES = [
    "appointment",
    "restaurant",
    "facility",
    "class",
    "vehicle-service",
    "field-service",
] as const;

export type BookingPortalProfile = typeof BOOKING_PORTAL_PROFILES[number];

/**
 * Accommodation needs stay/inventory semantics (arrival, departure, nights,
 * room-type inventory) and is therefore intentionally not selectable yet.
 */
export const FUTURE_BOOKING_PORTAL_PROFILES = ["accommodation"] as const;
export type FutureBookingPortalProfile = typeof FUTURE_BOOKING_PORTAL_PROFILES[number];

export type BookingPortalProfileFlowStep =
    | "service"
    | "partySize"
    | "resource"
    | "vehicle"
    | "intake"
    | "schedule"
    | "contact";
export type BookingPortalServiceSelection = "required" | "optional" | "implicit";
export type BookingPortalResourceSelection = "customer-choice" | "automatic" | "not-applicable";
export type BookingPortalCapacitySelection = "fixed-one" | "party-size";
export type BookingPortalIntake = "none" | "vehicle" | "generic";

export type BookingPortalProfileContract = {
    steps: readonly BookingPortalProfileFlowStep[];
    optionalSteps: readonly BookingPortalProfileFlowStep[];
    compatibleVariants: readonly BookingPortalVariant[];
    defaultVariant: BookingPortalVariant;
    serviceSelection: BookingPortalServiceSelection;
    resourceSelection: BookingPortalResourceSelection;
    capacitySelection: BookingPortalCapacitySelection;
    intake: BookingPortalIntake;
};

export const BOOKING_PORTAL_PROFILE_CONTRACTS: Readonly<
    Record<BookingPortalProfile, BookingPortalProfileContract>
> = {
    appointment: {
        steps: ["service", "resource", "schedule", "contact"],
        optionalSteps: ["resource"],
        compatibleVariants: ["guided", "schedule", "professional", "venue-grid", "quick-slots"],
        defaultVariant: "guided",
        serviceSelection: "required",
        resourceSelection: "customer-choice",
        capacitySelection: "fixed-one",
        intake: "none",
    },
    restaurant: {
        steps: ["partySize", "service", "schedule", "contact"],
        optionalSteps: ["service"],
        compatibleVariants: ["guided", "schedule", "quick-slots"],
        defaultVariant: "guided",
        serviceSelection: "optional",
        resourceSelection: "automatic",
        capacitySelection: "party-size",
        intake: "none",
    },
    facility: {
        steps: ["service", "resource", "schedule", "contact"],
        optionalSteps: [],
        compatibleVariants: ["guided", "schedule", "venue-grid", "quick-slots"],
        defaultVariant: "venue-grid",
        serviceSelection: "required",
        resourceSelection: "customer-choice",
        capacitySelection: "fixed-one",
        intake: "none",
    },
    class: {
        steps: ["service", "schedule", "contact"],
        optionalSteps: [],
        compatibleVariants: ["guided", "schedule", "quick-slots"],
        defaultVariant: "schedule",
        serviceSelection: "required",
        resourceSelection: "automatic",
        capacitySelection: "fixed-one",
        intake: "none",
    },
    "vehicle-service": {
        steps: ["service", "schedule", "contact"],
        optionalSteps: [],
        compatibleVariants: ["guided", "schedule", "quick-slots"],
        defaultVariant: "guided",
        serviceSelection: "required",
        resourceSelection: "automatic",
        capacitySelection: "fixed-one",
        intake: "vehicle",
    },
    "field-service": {
        steps: ["service", "schedule", "contact"],
        optionalSteps: [],
        compatibleVariants: ["guided", "schedule", "quick-slots"],
        defaultVariant: "guided",
        serviceSelection: "required",
        resourceSelection: "automatic",
        capacitySelection: "fixed-one",
        intake: "generic",
    },
};

export function normalizeBookingPortalProfile(value: unknown): BookingPortalProfile {
    return typeof value === "string"
        && (BOOKING_PORTAL_PROFILES as readonly string[]).includes(value)
        ? value as BookingPortalProfile
        : "appointment";
}

export function bookingPortalProfileContract(
    value: unknown,
): BookingPortalProfileContract {
    return BOOKING_PORTAL_PROFILE_CONTRACTS[normalizeBookingPortalProfile(value)];
}

export function bookingPortalProfileEntries(): ReadonlyArray<
    readonly [BookingPortalProfile, BookingPortalProfileContract]
> {
    return BOOKING_PORTAL_PROFILES.map((profile) => [
        profile,
        BOOKING_PORTAL_PROFILE_CONTRACTS[profile],
    ] as const);
}

export function isBookingPortalVariantCompatible(
    profileValue: unknown,
    variantValue: unknown,
): boolean {
    const profile = normalizeBookingPortalProfile(profileValue);
    const variant = normalizeBookingPortalVariant(variantValue);

    return BOOKING_PORTAL_PROFILE_CONTRACTS[profile].compatibleVariants.includes(variant);
}

export function normalizeBookingPortalVariantForProfile(
    profileValue: unknown,
    variantValue: unknown,
): BookingPortalVariant {
    const profile = normalizeBookingPortalProfile(profileValue);
    const contract = BOOKING_PORTAL_PROFILE_CONTRACTS[profile];
    const variant = normalizeBookingPortalVariant(variantValue);

    return contract.compatibleVariants.includes(variant)
        ? variant
        : contract.defaultVariant;
}
